import { Button, Cascader, Form, Input, message, Modal } from 'antd';
import { WrappedFormUtils } from 'antd/lib/form/Form';
import gql from 'graphql-tag';
import React, { Component } from 'react';
import { Mutation } from 'react-apollo';
import { provincesAndCities } from 'smartpi-core';

import { ALL_USERS_QUERY } from './Users';

interface EditableUserBasicInfo {
  realName: string;
  familyRole: string;
  age: string;
  remark: string;
  address: string;
  emergencyContact: string;
}

interface State {
  isShowModal: boolean;
  realName: string;
  familyRole: string;
  age?: number;
  remark: string;
  addressProvince: string;
  addressCity: string;
  addressDetail: string;
  addressConsignee: string;
  addressMobile?: number;
  primaryContact: string;
  secondaryContact: string;
  generalContact: string;
}

interface Props {
  form: WrappedFormUtils;
  userId: number;
  miniGameName: string;
  defaultUserBasicInfo: EditableUserBasicInfo;
}

const formItemLayout = {
  labelCol: {
    xs: { span: 24 },
    sm: { span: 7 },
  },
  wrapperCol: {
    xs: { span: 24 },
    sm: { span: 15 },
  },
};

const updateUserMetaMutation = gql`
  mutation updateUserMetas(
    $userId: ID!
    $realName: String
    $familyRole: String
    $age: String
    $remark: String
    $address: String
    $emergencyContact: String
  ) {
    updateUserMetas(
      userId: $userId
      realName: $realName
      familyRole: $familyRole
      age: $age
      remark: $remark
      address: $address
      emergencyContact: $emergencyContact
    ) {
      id
    }
  }
`;

class EditUserBasicInfo extends Component<Props, State> {
  constructor(props: Props) {
    super(props);
    const [primaryContact = '', secondaryContact = '', generalContact = ''] = this.props.defaultUserBasicInfo
      .emergencyContact
      ? this.props.defaultUserBasicInfo.emergencyContact.split('/')
      : '';

    const [province = '', city = '', detail = '', consignee = '', mobile = ''] = this.props.defaultUserBasicInfo
      .emergencyContact
      ? this.props.defaultUserBasicInfo.address.split('/')
      : '';

    this.state = {
      primaryContact,
      secondaryContact,
      generalContact,
      addressProvince: province,
      addressCity: city,
      addressDetail: detail,
      addressConsignee: consignee,
      addressMobile: mobile ? Number(mobile) : undefined,
      isShowModal: false,
      realName: this.props.defaultUserBasicInfo.realName,
      familyRole: this.props.defaultUserBasicInfo.familyRole,
      age: this.props.defaultUserBasicInfo.age ? Number(this.props.defaultUserBasicInfo.age) : undefined,
      remark: this.props.defaultUserBasicInfo.remark,
    };
  }

  componentDidMount() {
    this.props.form.validateFields();
  }

  hideModal = () => {
    this.setState({ isShowModal: false });
  };

  showModal = () => {
    this.setState({ isShowModal: true });
  };

  render() {
    const { userId, miniGameName } = this.props;
    const { getFieldDecorator } = this.props.form;
    const {
      isShowModal,
      realName,
      familyRole,
      age,
      remark,
      addressProvince,
      addressCity,
      addressDetail,
      addressConsignee,
      addressMobile,
      primaryContact,
      secondaryContact,
      generalContact,
    } = this.state;
    const defaultValueCity = (addressProvince && addressCity && [addressProvince, addressCity]) || [];
    const cityOptions = provincesAndCities.map(item => {
      const children = item.cities.map(value => {
        return {
          value,
          label: value,
        };
      });
      const option = { children, value: item.name, label: item.name };
      return option;
    });
    let emergencyContact = '';
    if (primaryContact || secondaryContact || generalContact) {
      emergencyContact = [primaryContact, secondaryContact, generalContact]
        .map(value => `${value}`.replace(/\//g, ''))
        .join('/');
    }
    let address = '';
    if (addressProvince || addressCity || addressDetail || addressConsignee || addressMobile) {
      const mobileStr = addressMobile ? `${addressMobile}` : '';
      address = [addressProvince, addressCity, addressDetail, addressConsignee, mobileStr]
        .map(value => `${value}`.replace(/\//g, ''))
        .join('/');
    }

    return (
      <div>
        <Button type="primary" icon="edit" onClick={this.showModal}>
          编辑
        </Button>
        <Mutation
          mutation={updateUserMetaMutation}
          variables={{ userId, realName, familyRole, age, remark, address, emergencyContact }}
          refetchQueries={[{ query: ALL_USERS_QUERY, variables: { miniGameName, ids: [userId] } }]}
        >
          {(updateWorkerUser, { loading }) => {
            return (
              <Modal
                title="编辑用户信息"
                visible={isShowModal}
                confirmLoading={loading}
                onOk={() => {
                  this.props.form.validateFields(async (err, values) => {
                    if (err) {
                      message.error(err);
                      return;
                    }
                    const age = Number(values.age);
                    const addressMobile = Number(values.mobile);
                    if (isNaN(age)) {
                      message.error('年龄是数字哦!');
                      return;
                    }
                    if (isNaN(addressMobile)) {
                      message.error('手机号是数字哦!');
                      return;
                    }
                    let province = addressProvince;
                    let city = addressCity;
                    if (values.provinceCity && values.provinceCity[0] && values.provinceCity[1]) {
                      province = values.provinceCity[0];
                      city = values.provinceCity[1];
                    }
                    this.setState(
                      {
                        age,
                        addressMobile,
                        realName: values.realName,
                        familyRole: values.familyRole,
                        remark: values.remark,
                        addressProvince: province,
                        addressCity: city,
                        addressDetail: values.detail,
                        addressConsignee: values.consignee,
                        primaryContact: values.emergencyContact1,
                        secondaryContact: values.emergencyContact2,
                        generalContact: values.emergencyContact3,
                      },
                      async () => {
                        await updateWorkerUser();
                        this.setState({ isShowModal: false });
                        message.success('更新成功！');
                      },
                    );
                  });
                }}
                onCancel={this.hideModal}
              >
                <Form>
                  <Form.Item {...formItemLayout} label="姓名">
                    {getFieldDecorator('realName', { initialValue: realName || '' })(
                      <Input placeholder="请输入姓名" />,
                    )}
                  </Form.Item>

                  <Form.Item {...formItemLayout} label="家庭角色">
                    {getFieldDecorator('familyRole', { initialValue: familyRole || '' })(
                      <Input placeholder="请输入家庭角色" />,
                    )}
                  </Form.Item>

                  <Form.Item {...formItemLayout} label="年龄">
                    {getFieldDecorator('age', { initialValue: age })(<Input placeholder="请输入年龄" />)}
                  </Form.Item>

                  <Form.Item {...formItemLayout} label="备注">
                    {getFieldDecorator('remark', { initialValue: remark || '' })(<Input placeholder="请输入备注" />)}
                  </Form.Item>

                  <Form.Item {...formItemLayout} label="地址">
                    {getFieldDecorator('provinceCity', { initialValue: defaultValueCity })(
                      <Cascader options={cityOptions} placeholder="请选择" />,
                    )}
                  </Form.Item>
                  <Form.Item {...formItemLayout} label="详情">
                    {getFieldDecorator('detail', { initialValue: addressDetail || '' })(
                      <Input placeholder="请输入详情" />,
                    )}
                  </Form.Item>
                  <Form.Item {...formItemLayout} label="收件人">
                    {getFieldDecorator('consignee', { initialValue: addressConsignee || '' })(
                      <Input placeholder="请输入收件人" />,
                    )}
                  </Form.Item>
                  <Form.Item {...formItemLayout} label="收件人手机号">
                    {getFieldDecorator('mobile', { initialValue: addressMobile })(<Input placeholder="请输入手机号" />)}
                  </Form.Item>

                  <Form.Item {...formItemLayout} label="紧急联系人1信息">
                    {getFieldDecorator('emergencyContact1', { initialValue: primaryContact || '' })(
                      <Input placeholder="请输入紧急联系人信息" />,
                    )}
                  </Form.Item>
                  <Form.Item {...formItemLayout} label="紧急联系人2信息">
                    {getFieldDecorator('emergencyContact2', { initialValue: secondaryContact || '' })(
                      <Input placeholder="请输入紧急联系人信息" />,
                    )}
                  </Form.Item>
                  <Form.Item {...formItemLayout} label="紧急联系人3信息">
                    {getFieldDecorator('emergencyContact3', { initialValue: generalContact || '' })(
                      <Input placeholder="请输入紧急联系人信息" />,
                    )}
                  </Form.Item>
                </Form>
              </Modal>
            );
          }}
        </Mutation>
      </div>
    );
  }
}

export { EditableUserBasicInfo, EditUserBasicInfo };



// WEBPACK FOOTER //
// ./src/screens/user/EditUserBasicInfo.tsx